import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  effect,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  relationshipConditionOptions,
  getRelationshipRelationOptions,
  relationshipTargetOptions,
  type ObjectSearchSuggestion,
  defaultRelationFilterValue,
} from '@bizzdesign/data-access-search';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  type BDTreeNode,
  type DropdownSettings,
  type RelationCondition,
  type RelationFilterValue,
  type RelationTarget,
} from '@bizzdesign/shared/models';
import { createBDCoreObjectTypeOptions } from '@bizzdesign/shared/utils';
import { DropdownComponent } from '@bizzdesign/ui-dropdown';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ObjectAutocompleteComponent } from '@bizzdesign/feature-content-details';
import { type AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { AdvancedFilterRelationshipComponentStore } from './advanced-filter-relationship.component.store';
import { cloneDeep } from 'lodash-es';
import { NgClass } from '@angular/common';
import { isNotBlank } from '@bizzdesign/bizzdesign-sdk';
import { isUUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import { type ObjectType } from '@bizzdesign/sdk-knowledgegraph/bdcore';
@Component({
  selector: 'bd-advanced-filter-relationship',
  imports: [
    NgClass,
    InputTextModule,
    SharedI18nModule,
    FormsModule,
    TooltipModule,
    ButtonModule,
    DividerModule,
    DropdownComponent,
    ObjectAutocompleteComponent,
  ],
  providers: [AdvancedFilterRelationshipComponentStore],
  templateUrl: './advanced-filter-relationship.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedFilterRelationshipComponent {
  readonly store = inject(AdvancedFilterRelationshipComponentStore);
  initialFilterValue = input<RelationFilterValue>(defaultRelationFilterValue);

  submitRelationFilter = output<RelationFilterValue>();
  removeRelationFilter = output<RelationFilterValue>();

  filterValue = signal<RelationFilterValue>(this.initialFilterValue());
  readonly conditionOptions = signal<BDTreeNode[]>(
    relationshipConditionOptions,
  );
  readonly targetOptions = signal<BDTreeNode[]>(relationshipTargetOptions);
  readonly relationOptions = signal<BDTreeNode[]>(
    getRelationshipRelationOptions(),
  );
  readonly objectTypeOptions = signal<BDTreeNode[]>(
    createBDCoreObjectTypeOptions(),
  );

  selectedTarget = model<BDTreeNode<RelationTarget> | undefined>();
  selectedCondition = model<BDTreeNode<RelationCondition> | undefined>();
  selectedRelation = model<BDTreeNode<string> | undefined>();
  selectedObjectType = model<BDTreeNode<ObjectType> | undefined>();
  selectedObject = signal<ObjectSearchSuggestion | undefined>(undefined);
  autoFocusTargetDropdown = signal(false);
  autoFocusObjectTypeDropdown = signal(false);
  autoFocusRelationDropdown = signal(false);
  autoFocusObjectAutocomplete = signal(false);

  objectSelectionDisabled = computed(
    () =>
      !this.selectedTarget() ||
      (this.selectedTarget()?.key !== 'specificObject' &&
        this.selectedTarget()?.key !== 'objectType'),
  );

  readonly sharedDropdownSettings: DropdownSettings = {
    selectionMode: 'single',
    inputType: 'default',
    layout: 'list',
    dropdownWidth: '182px',
  };

  readonly relationDropdownSettings: DropdownSettings = {
    ...this.sharedDropdownSettings,
    tScope: 'featureSearch',
    inputPlaceholderKey: 'relationshipFilter.relationDropdownPlaceholder',
    dropdownLabelKey: 'relationshipFilter.relationDropdownLabel',
  };

  readonly targetDropdownSettings: DropdownSettings = {
    ...this.sharedDropdownSettings,
    tScope: 'featureSearch',
    inputPlaceholderKey: 'relationshipFilter.targetDropdownPlaceholder',
    dropdownLabelKey: 'relationshipFilter.targetDropdownLabel',
  };

  readonly objectTypeDropdownSettings = computed<DropdownSettings>(() => {
    const noTargetObject =
      this.selectedTarget() &&
      (this.selectedTarget()?.key === 'noObject' ||
        this.selectedTarget()?.key === 'anyObject');
    return {
      ...this.sharedDropdownSettings,
      tScope: 'featureSearch',
      inputPlaceholderKey: noTargetObject
        ? undefined
        : 'relationshipFilter.objectTypeDropdownPlaceholder',
      inputPlaceholder: noTargetObject ? '-' : undefined,
      dropdownLabelKey: 'relationshipFilter.objectTypeDropdownLabel',
    };
  });

  readonly conditionDropdownSettings: DropdownSettings = {
    ...this.sharedDropdownSettings,
    tScope: 'featureSearch',
    inputPlaceholderKey: 'relationshipFilter.conditionDropdownPlaceholder',
    dropdownLabelKey: 'relationshipFilter.conditionDropdownLabel',
  };

  constructor() {
    effect(() => {
      this.filterValue.set(cloneDeep(this.initialFilterValue()));
      if (isNotBlank(this.initialFilterValue().id)) {
        this.setInitialValues(this.initialFilterValue());
      } else {
        this.resetDropdowns();
      }
    });
  }

  setInitialValues(initialFilterValue: RelationFilterValue) {
    this.selectedCondition.set(
      this.conditionOptions().find(
        (condition) => condition.key === initialFilterValue.condition,
      ),
    );
    this.selectedRelation.set(
      this.relationOptions().find(
        (relation) =>
          relation.key ===
          (initialFilterValue.relation.roleName || 'anyRelation'),
      ),
    );

    this.selectedTarget.set(
      this.targetOptions().find(
        (target) => target.key === initialFilterValue.target,
      ),
    );

    // Both selections are target-specific, so clear them before rehydrating the incoming target's.
    this.selectedObject.set(undefined);
    this.selectedObjectType.set(undefined);

    if (initialFilterValue.target === 'specificObject') {
      const relatedElement = initialFilterValue.relation.relatedElement;
      const relatedObjectId = relatedElement?.id?.[0];
      // The SDK role filter stores ids as plain strings, so validate before rehydrating.
      if (isUUID(relatedObjectId)) {
        this.selectedObject.set({
          id: relatedObjectId,
          name: relatedElement?.name?.[0] ?? '',
          elementType: relatedElement?.type?.[0] ?? '',
          icon: '',
        });
      }
    } else if (initialFilterValue.target === 'objectType') {
      this.selectedObjectType.set(
        this.objectTypeOptions().find(
          (objectType) =>
            objectType.key ===
            initialFilterValue.relation.relatedElement?.type?.[0],
        ),
      );
    }
  }

  removeFilter() {
    this.removeRelationFilter.emit(this.filterValue());
    this.filterValue.set({ ...defaultRelationFilterValue });
    this.resetDropdowns();
  }

  resetDropdowns() {
    this.selectedObjectType.set(undefined);
    this.selectedObject.set(undefined);
    this.selectedCondition.set(undefined);
    this.selectedRelation.set(undefined);
    this.selectedTarget.set(undefined);
  }

  submitSelectedObject(selectedSuggestion?: AutoCompleteSelectEvent) {
    const selectedObject: ObjectSearchSuggestion | undefined =
      selectedSuggestion?.value;
    this.selectedObject.set(selectedObject);
    this.filterValue.set({
      ...this.filterValue(),
      relation: {
        ...this.filterValue().relation,
        relatedElement: selectedObject
          ? {
              id: [selectedObject.id],
              name: [selectedObject.name],
              type: [selectedObject.elementType],
            }
          : {},
      },
    });

    this.submitFilter();
  }

  searchObjects(term: string) {
    // If the term did not change, we do not want to reset the suggestions
    // We do want to update the state as autocomplete expects a new array
    if (term !== this.store.objectSearchStoreProperties().searchTerm) {
      this.store.updateSearchSuggestions([]);
    } else {
      this.store.updateSearchSuggestions(
        cloneDeep(this.store.objectSearchStoreProperties().searchSuggestions),
      );
    }
    this.store.updateSearchTerm(term);
  }

  onConditionChange(selected?: BDTreeNode) {
    if (selected) {
      this.filterValue.set({
        ...this.filterValue(),
        condition: selected.key as RelationCondition,
      });
      if (this.filterValue().id !== '') {
        this.submitFilter();
      }
      this.autoFocusRelationDropdown.set(true);
    } else {
      this.removeFilter();
    }
  }

  onRelationChange(selected?: BDTreeNode) {
    if (selected) {
      this.filterValue.set({
        ...this.filterValue(),
        relation: {
          ...this.filterValue().relation,
          roleName: selected.key === 'anyRelation' ? '' : selected.key,
        },
      });
      this.autoFocusTargetDropdown.set(true);
    } else {
      this.filterValue.set({
        ...this.filterValue(),
        target: undefined,
        relation: {},
      });
      this.selectedTarget.set(undefined);
      this.selectedObjectType.set(undefined);
      this.selectedObject.set(undefined);
    }

    if (this.filterValue().id !== '') {
      this.submitFilter();
    }
  }

  onTargetChange(selected?: BDTreeNode) {
    const target = selected?.key as RelationTarget;
    this.filterValue.set({
      ...this.filterValue(),
      target,
      relation: {
        ...this.filterValue().relation,
        relatedElement: {},
      },
    });
    const isLastElement = target === 'anyObject' || target === 'noObject';
    if (isLastElement || this.filterValue().id !== '') {
      this.submitFilter();
    }

    if (selected && !isLastElement) {
      this.autoFocusObjectTypeDropdown.set(true);
      this.autoFocusObjectAutocomplete.set(true);
    }

    this.selectedObjectType.set(undefined);
    this.selectedObject.set(undefined);
  }

  onObjectTypeChange(selected?: BDTreeNode) {
    this.filterValue.set({
      ...this.filterValue(),
      relation: {
        ...this.filterValue().relation,
        relatedElement: { type: selected ? [selected.key!] : [] },
      },
    });
    this.submitFilter();
  }

  submitFilter() {
    this.submitRelationFilter.emit(this.filterValue());
  }
}
